// ManDoorsDialog.tsx — Up to 6 man doors (drvar)

import DialogBase from './DialogBase';
import { SlotEditor } from './SlotEditor';
import { usePendingSlot } from './usePendingSlot';
import { SizePalette, DOOR_PRESETS } from '../utils/sizePalette';
import type { SizePreset } from '../utils/sizePalette';
import { useShallowStore } from '../store/useStore';
import type { DrData } from '../types/panelvar';

const FIELDS = [
  { key: 'mark',   label: 'Mark',       type: 'text'   as const },
  { key: 'width',  label: 'Width',      type: 'number' as const, unit: '"', min: 0 },
  { key: 'height', label: 'Height',     type: 'number' as const, unit: '"', min: 0 },
  { key: 'x',      label: 'X Ctr',      type: 'number' as const, unit: '"', min: 0 },
  { key: 'y',      label: 'Sill Elev',  type: 'number' as const, unit: '"', min: 0 },
];

export default function ManDoorsDialog() {
  const { ui, project, updatePanel } = useShallowStore(s => ({
    ui: s.ui, project: s.project, updatePanel: s.updatePanel,
  }));
  const panel = project?.panels.find(p => p.id === ui.activePanelId);
  const [slots, setSlots] = usePendingSlot<DrData>('drvar', panel?.drvar ?? [],
    (px, py, mark) => ({ mark, x: px, y: py }),
    { mark: '', type: 'single' as const, x: 0, y: 0, width: 36, height: 84, threshold: 2, jamb: 1 });
  if (!panel) return null;

  const panelW = panel.mpvar.width || 360;

  function addFromPalette(p: SizePreset) {
    const data: DrData = {
      mark: '', type: (p.type ?? 'single') as DrData['type'],
      x: panelW / 2, y: 0,
      width: p.width, height: p.height,
      threshold: 2, jamb: 1,
    };
    setSlots(prev => {
      const idx = prev.findIndex(s => !s.enabled);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { enabled: true, data };
        return next;
      }
      if (prev.length < 6) return [...prev, { enabled: true, data }];
      return prev;
    });
  }

  return (
    <DialogBase title="Man Doors (6 max)" onOk={() => updatePanel(panel.id, { drvar: slots })}>
      <SizePalette title="Common Door Sizes" presets={DOOR_PRESETS} onSelect={addFromPalette} />
      <SlotEditor
        slots={slots}
        fields={FIELDS}
        maxSlots={6}
        onChange={v => setSlots(v)}
        defaultData={() => ({ mark: '', type: 'single' as const, x: panelW / 2, y: 0, width: 36, height: 84, threshold: 2, jamb: 1 })}
        label="Door"
      />
    </DialogBase>
  );
}
